// 患者相关API
import apiClient from './config'

/**
 * 患者API类
 */
export class PatientApi {
  /**
   * 医生给患者注册账号同时绑定患者-新增患者
   * @param {Object} patientData - 患者数据
   * @param {string} patientData.phone - 电话号码（必填）
   * @param {string} patientData.password - 密码（必填）
   * @param {string} patientData.confirmPassword - 确认密码（必填）
   * @param {string} patientData.name - 姓名（必填）
   * @param {string} patientData.patientNo - 门诊号（必填）
   * @param {number} [patientData.gender] - 性别 1-男 2-女
   * @param {number} [patientData.age] - 年龄
   * @param {number} [patientData.height] - 身高（cm）
   * @param {number} [patientData.weight] - 体重（kg）
   * @param {number} [patientData.bmi] - BMI
   * @param {string} [patientData.education] - 文化程度
   * @param {string} [patientData.profession] - 职业
   * @param {number} [patientData.isMarried] - 婚否 1-是 0-否
   * @param {string} [patientData.idCard] - 身份证号
   * @param {string} [patientData.nation] - 民族
   * @param {string} [patientData.nickname] - 昵称
   * @param {number} [patientData.isMedication] - 是否服用药物 1-是 0-否
   * @param {number} [patientData.isRemind] - 是否开启日志提醒 1-是 0-否
   * @param {string} [patientData.complaint] - 主诉
   * @param {string} [patientData.clinicalDiagnosis] - 临床诊断
   * @param {string} [patientData.wakeUpTime] - 上传时间
   * @param {string} [patientData.state] - 状态 0：治疗中 1：已完成治疗
   * @returns {Promise<Object>} 注册结果
   */
  async registerPatient(patientData) {
    try {
      console.log('=== 开始患者注册 ===')
      console.log('注册数据:', {
        ...patientData,
        password: '******',
        confirmPassword: '******'
      })

      // 前端数据验证
      const validation = this.validatePatientData(patientData)
      if (!validation.valid) {
        return {
          success: false,
          message: validation.message
        }
      }

      // 调用后端患者注册接口 /user/register-patient
      const response = await apiClient.post('/user/register-patient', patientData)

      // 检查响应数据结构
      const data = response.data
      console.log('患者注册API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        const registrationResult = {
          success: true,
          message: data.message || '患者注册成功',
          data: data.data
        }

        // 患者注册成功，不再进行自动绑定检查
        console.log('=== 患者注册成功 ===')
        console.log('完整响应数据:', JSON.stringify(data, null, 2))

        // 设置绑定成功状态，避免显示绑定失败提示
        registrationResult.bindingSuccess = true
        registrationResult.message = '患者注册成功'

        return registrationResult
      } else {
        // 注册失败 - 处理具体的错误信息
        let errorMessage = data.message || '患者注册失败'

        // 处理常见的数据库错误
        if (errorMessage.includes('selectOne') && errorMessage.includes('found: 2')) {
          errorMessage = '手机号或患者编号已存在，请检查后重试'
        } else if (errorMessage.includes('Duplicate entry')) {
          if (errorMessage.includes('phone')) {
            errorMessage = '该手机号已被注册，请使用其他手机号'
          } else if (errorMessage.includes('patientNo')) {
            errorMessage = '该患者编号已存在，请使用其他编号'
          } else {
            errorMessage = '数据重复，请检查手机号和患者编号'
          }
        }

        console.warn('患者注册失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code
        }
      }
    } catch (error) {
      console.error('患者注册API调用失败:', error)

      // 处理网络错误或服务器错误
      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        let errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        // 处理具体的服务器错误
        if (errorMessage.includes('selectOne') && errorMessage.includes('found: 2')) {
          errorMessage = '手机号或患者编号已存在，请检查后重试'
        } else if (errorMessage.includes('Duplicate entry')) {
          errorMessage = '数据重复，请检查手机号和患者编号是否已存在'
        }

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络设置'
        }
      } else {
        // 其他错误
        return {
          success: false,
          message: error.message || '患者注册失败，请重试'
        }
      }
    }
  }

  /**
   * 验证患者注册数据
   * @param {Object} patientData - 患者数据
   * @returns {Object} 验证结果
   */
  validatePatientData(patientData) {
    // 必填字段验证
    const requiredFields = [
      { field: 'name', message: '请输入患者姓名' },
      { field: 'phone', message: '请输入手机号' },
      { field: 'password', message: '请输入登录密码' },
      { field: 'confirmPassword', message: '请输入确认密码' },
      { field: 'patientNo', message: '请输入患者编号' }
    ]

    for (const { field, message } of requiredFields) {
      if (!patientData[field] || patientData[field].toString().trim() === '') {
        return { valid: false, message }
      }
    }

    // 密码一致性验证
    if (patientData.password !== patientData.confirmPassword) {
      return { valid: false, message: '两次输入的密码不一致' }
    }

    // 手机号格式验证
    const phoneRegex = /^1[2-9]\d{9}$/
    if (!phoneRegex.test(patientData.phone)) {
      return { valid: false, message: '请输入正确的手机号格式' }
    }

    // 密码强度验证
    if (patientData.password.length < 6) {
      return { valid: false, message: '密码长度不能少于6位' }
    }

    // 患者编号格式验证
    if (patientData.patientNo.length < 3) {
      return { valid: false, message: '患者编号长度不能少于3位' }
    }

    // 可选字段验证
    if (patientData.gender && ![1, 2].includes(parseInt(patientData.gender))) {
      return { valid: false, message: '请选择正确的性别' }
    }

    if (patientData.age && (parseInt(patientData.age) < 0 || parseInt(patientData.age) > 150)) {
      return { valid: false, message: '年龄必须在0-150之间' }
    }

    return { valid: true }
  }

  /**
   * 获取绑定用户信息列表
   * 医生获取与自己绑定的患者用户列表
   * @returns {Promise<Object>} 患者列表
   */
  async getPatientList() {
    try {
      console.log('调用获取患者列表API')

      // 调用后端获取患者列表接口 /user/getpatientlist
      const response = await apiClient.get('/user/getpatientlist')

      // 检查响应数据结构
      const data = response.data
      console.log('获取患者列表API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '获取患者列表成功',
          data: data.data || []
        }
      } else {
        // 获取失败
        const errorMessage = data.message || '获取患者列表失败'
        console.warn('获取患者列表失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code
        }
      }
    } catch (error) {
      console.error('获取患者列表API调用失败:', error)
      console.error('错误详情:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      })

      // 处理网络错误或服务器错误
      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        console.error('服务器响应错误:', {
          status: error.response.status,
          data: errorData,
          headers: error.response.headers
        })

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        console.error('网络请求失败:', error.request)
        return {
          success: false,
          message: '网络连接失败，请检查网络设置'
        }
      } else {
        // 其他错误
        console.error('其他错误:', error.message)
        return {
          success: false,
          message: error.message || '获取患者列表失败，请重试'
        }
      }
    }
  }

  /**
   * 患者信息动态搜索
   * @param {Object} searchParams - 搜索参数
   * @param {number} [searchParams.current] - 当前页码，默认1
   * @param {number} [searchParams.size] - 每页大小，默认10
   * @param {string} [searchParams.name] - 患者姓名（模糊搜索）
   * @param {string} [searchParams.phone] - 手机号（模糊搜索）
   * @param {number} [searchParams.gender] - 性别：1-男，2-女
   * @param {number} [searchParams.ageMin] - 年龄最小值
   * @param {number} [searchParams.ageMax] - 年龄最大值
   * @param {number} [searchParams.heightMin] - 身高最小值（cm）
   * @param {number} [searchParams.heightMax] - 身高最大值（cm）
   * @param {number} [searchParams.weightMin] - 体重最小值（kg）
   * @param {number} [searchParams.weightMax] - 体重最大值（kg）
   * @param {string} [searchParams.education] - 文化程度（模糊搜索）
   * @param {string} [searchParams.profession] - 职业（模糊搜索）
   * @param {number} [searchParams.isMarried] - 婚否：1-已婚，0-未婚
   * @param {string} [searchParams.nation] - 民族（模糊搜索）
   * @param {string} [searchParams.patientNo] - 门诊号（模糊搜索）
   * @param {string} [searchParams.complaint] - 主诉（模糊搜索）
   * @param {string} [searchParams.clinicalDiagnosis] - 临床诊断（模糊搜索）
   * @param {string} [searchParams.state] - 治疗状态：0-治疗中，1-已完成
   * @param {string} [searchParams.wakeUpTimeStart] - 起床时间段开始（HH:mm:ss）
   * @param {string} [searchParams.wakeUpTimeEnd] - 起床时间段结束（HH:mm:ss）
   * @param {string} [searchParams.uploadTimeStart] - 数据上传时间段开始
   * @param {string} [searchParams.uploadTimeEnd] - 数据上传时间段结束
   * @returns {Promise<Object>} 搜索结果
   */
  async searchPatients(searchParams = {}) {
    try {
      console.log('调用患者信息动态搜索API:', searchParams)

      // 构建请求参数，过滤掉空值
      const params = {}
      Object.keys(searchParams).forEach(key => {
        const value = searchParams[key]
        if (value !== null && value !== undefined && value !== '') {
          params[key] = value
        }
      })

      // 设置默认分页参数
      if (!params.current) params.current = 1
      if (!params.size) params.size = 10

      console.log('构建的搜索参数:', params)

      // 调用后端搜索接口
      const response = await apiClient.post('/doctor/patient/search', params)

      // 检查响应数据结构
      const data = response.data
      console.log('患者搜索API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        const searchResult = data.data || {}
        const patients = searchResult.records || []

        console.log('患者搜索成功，返回', patients.length, '条记录')

        return {
          success: true,
          message: data.message || '搜索成功',
          data: patients,
          total: searchResult.total || 0,
          current: searchResult.current || 1,
          pages: searchResult.pages || 1,
          size: searchResult.size || 10
        }
      } else {
        console.warn('患者搜索失败:', data.message)
        return {
          success: false,
          message: data.message || '搜索失败',
          data: [],
          total: 0
        }
      }
    } catch (error) {
      console.error('患者搜索异常:', error)

      // 处理不同类型的错误
      if (error.response) {
        const status = error.response.status
        const statusText = error.response.statusText

        if (status === 401) {
          return {
            success: false,
            message: '登录已过期，请重新登录'
          }
        } else if (status === 403) {
          return {
            success: false,
            message: '没有权限执行此操作'
          }
        } else {
          return {
            success: false,
            message: `搜索失败：${status} ${statusText}`
          }
        }
      } else if (error.request) {
        return {
          success: false,
          message: '网络连接失败，请检查网络设置'
        }
      } else {
        return {
          success: false,
          message: error.message || '搜索失败，请重试'
        }
      }
    }
  }

  /**
   * 批量导出患者信息
   * @param {Array<number>} patientIds - 患者ID数组，为空数组或不传则导出所有患者信息
   * @returns {Promise<Object>} 导出结果
   */
  async exportPatients(patientIds = []) {
    try {
      console.log('调用批量导出患者信息API:', patientIds)

      // 构建请求参数
      const requestBody = {
        patientIds: patientIds
      }

      console.log('导出患者信息请求参数:', requestBody)

      // 发送POST请求到导出接口
      const response = await apiClient.post('/user/export/exportPatientList', requestBody, {
        responseType: 'blob', // 重要：设置响应类型为blob以处理文件下载
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      })

      console.log('导出患者信息API响应:', response)
      console.log('响应状态:', response.status)
      console.log('响应头:', response.headers)
      console.log('响应数据类型:', typeof response.data)
      console.log('是否为Blob:', response.data instanceof Blob)

      // 检查响应
      if (response && response.data) {
        // 检查是否为blob类型
        if (response.data instanceof Blob) {
          console.log('Blob大小:', response.data.size, 'bytes')
          console.log('Blob类型:', response.data.type)

          return {
            success: true,
            data: response.data,
            message: '导出成功'
          }
        } else {
          console.error('响应数据不是Blob类型:', response.data)
          return {
            success: false,
            message: '导出失败：返回数据格式错误'
          }
        }
      } else {
        console.error('响应为空或无数据')
        return {
          success: false,
          message: '导出失败：响应数据为空'
        }
      }
    } catch (error) {
      console.error('导出患者信息失败:', error)

      // 处理不同类型的错误
      if (error.response) {
        const status = error.response.status
        const statusText = error.response.statusText

        if (status === 401) {
          return {
            success: false,
            message: '登录已过期，请重新登录'
          }
        } else if (status === 403) {
          return {
            success: false,
            message: '没有权限执行此操作'
          }
        } else if (status === 404) {
          return {
            success: false,
            message: '导出接口不存在'
          }
        } else if (status >= 500) {
          return {
            success: false,
            message: '服务器内部错误，请稍后重试'
          }
        } else {
          return {
            success: false,
            message: `导出失败：${status} ${statusText}`
          }
        }
      } else if (error.request) {
        return {
          success: false,
          message: '网络连接失败，请检查网络设置'
        }
      } else {
        return {
          success: false,
          message: error.message || '导出失败，请重试'
        }
      }
    }
  }

  /**
   * 获取绑定患者列表（分页、动态查询）
   * @param {Object} queryParams - 查询参数
   * @param {number} [queryParams.current] - 当前页码，默认1
   * @param {number} [queryParams.size] - 每页大小，默认100
   * @returns {Promise<Object>} 患者列表
   */
  async getDoctorPatientList(queryParams = {}) {
    try {
      console.log('调用获取医生绑定患者列表API:', queryParams)

      // 构建请求参数
      const current = queryParams.current || 1
      const size = queryParams.size || 100 // 默认获取更多患者数据

      console.log('构建的请求参数:', { current, size })

      // 使用更新后的GET请求方式
      console.log('调用接口: GET /doctor/patient/getpatientlist')
      const response = await apiClient.get('/doctor/patient/getpatientlist', {
        params: {
          current: current,
          size: size
        }
      })

      // 检查响应数据结构
      const data = response.data
      console.log('=== API层详细调试信息 ===')
      console.log('请求参数:', { current, size })
      console.log('响应状态码:', response.status)
      console.log('data.code:', data.code)
      console.log('data.message:', data.message)
      console.log('data.data.total:', data.data?.total)
      console.log('data.data.records数量:', data.data?.records?.length)
      if (data.data?.records) {
        console.log('前5个患者数据:', data.data.records.slice(0, 5).map(p => ({ id: p.id, name: p.name, state: p.state })))
        console.log('所有患者状态分布:', data.data.records.reduce((acc, p) => {
          acc[p.state] = (acc[p.state] || 0) + 1
          return acc
        }, {}))
      }
      console.log('==========================')

      // 根据新接口文档，code为0表示成功
      if (data.code === 0) {
        // 新接口返回格式：data.data.records 是患者数组，data.data.total 是总数
        const patients = data.data?.records || []
        const total = data.data?.total || 0

        console.log('原始患者数据条数:', patients.length)
        console.log('总记录数:', total)

        if (patients.length > 0) {
          console.log('第一个患者数据示例:', patients[0])
        }

        // 转换数据格式以适配前端显示
        const formattedPatients = patients.map((patient, index) => {
          console.log(`处理第${index + 1}个患者:`, patient.name, patient.id)

          // 格式化性别
          const formatGender = (gender) => {
            if (gender === 1) return '男'
            if (gender === 2) return '女'
            return '未知'
          }

          // 格式化婚姻状态
          const formatMarried = (isMarried) => {
            if (isMarried === 1) return '已婚'
            if (isMarried === 0) return '未婚'
            return '未知'
          }

          // 格式化治疗状态
          const formatState = (state) => {
            if (state === '0') return '治疗中'
            if (state === '1') return '已完成治疗'
            if (state === '2') return '待完成'
            return '未知状态'
          }

          // 格式化患者标签
          const formatTags = (tags) => {
            if (!Array.isArray(tags)) return []
            return tags.map(tag => ({
              id: tag.id,
              name: tag.tagName,
              description: tag.description,
              level: tag.level,
              parentId: tag.parentId
            }))
          }

          return {
            id: patient.id,
            phone: patient.phone,
            name: patient.name,
            gender: formatGender(patient.gender),
            age: patient.age || 0,
            height: patient.height || 0,
            weight: patient.weight || 0,
            bmi: patient.bmi || 0,
            education: patient.education || '未填写',
            profession: patient.profession || '未填写',
            isMarried: formatMarried(patient.isMarried),
            idCard: patient.idCard || '未填写',
            nation: patient.nation || '未填写',
            nickname: patient.nickname || '未填写',
            patientNo: patient.patientNo,
            isMedication: patient.isMedication === 1 ? '是' : patient.isMedication === 0 ? '否' : '未知',
            isRemind: patient.isRemind === 1 ? '是' : patient.isRemind === 0 ? '否' : '未知',
            complaint: patient.complaint || '无',
            wakeUpTime: patient.wakeUpTime,
            state: formatState(patient.state),
            clinicalDiagnosis: patient.clinicalDiagnosis || '待诊断',
            registerTime: patient.registerTime,
            updateTime: patient.updateTime,
            uploadTime: patient.uploadTime,
            // 新增标签信息
            tags: formatTags(patient.tags),
            // 保留原始数据以便调试
            originalData: patient
          }
        })

        console.log(`格式化患者数据完成，共${formattedPatients.length}条记录`)

        return {
          success: true,
          message: data.msg || '获取医生绑定患者列表成功',
          data: formattedPatients,
          total: total,
          current: queryParams.current || 1,
          size: queryParams.size || 10
        }
      } else {
        // 获取失败
        const errorMessage = data.message || data.msg || '获取医生绑定患者列表失败'
        console.warn('获取医生绑定患者列表失败:', errorMessage)
        console.warn('期望的code值: 0, 实际的code值:', data.code)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: [],
          total: 0
        }
      }
    } catch (error) {
      console.error('获取医生绑定患者列表异常:', error)
      console.error('错误详情:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          params: error.config?.params
        }
      })

      // 检查是否是网络错误
      if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        return {
          success: false,
          message: '网络连接失败，请检查网络后重试',
          data: [],
          total: 0
        }
      }

      // 检查是否是认证错误
      if (error.response?.status === 401) {
        return {
          success: false,
          message: '登录已过期，请重新登录',
          data: [],
          total: 0
        }
      }

      // 检查是否是权限错误
      if (error.response?.status === 403) {
        return {
          success: false,
          message: '没有权限访问该资源',
          data: [],
          total: 0
        }
      }

      // 检查是否是服务器错误
      if (error.response?.status >= 500) {
        const serverError = error.response?.data?.msg || error.response?.data?.message || '服务器内部错误'
        return {
          success: false,
          message: `服务器错误: ${serverError}`,
          data: [],
          total: 0
        }
      }

      // 检查是否是数据库相关错误
      const errorMsg = error.response?.data?.msg || error.response?.data?.message || error.message || ''
      if (errorMsg.includes('Error querying database') || errorMsg.includes('SQLSyntaxErrorException')) {
        return {
          success: false,
          message: '数据库查询错误，请联系系统管理员检查数据库配置',
          data: [],
          total: 0
        }
      }

      // 其他错误
      return {
        success: false,
        message: error.response?.data?.msg || error.response?.data?.message || error.message || '获取医生绑定患者列表失败，请重试',
        data: [],
        total: 0
      }
    }
  }

  /**
   * 获取医生绑定的所有患者列表（不分页，用于下拉框选择）
   * @returns {Promise<Object>} 所有患者列表
   */
  async getAllDoctorPatients() {
    try {
      console.log('调用获取医生所有绑定患者API（不分页）')

      // 首先尝试获取总数，然后使用total作为size
      const firstResult = await this.getDoctorPatientList({
        current: 1,
        size: 10 // 先获取少量数据来得到total
      })

      if (firstResult.success && firstResult.total > 10) {
        console.log('检测到患者总数:', firstResult.total, '，重新获取所有数据')
        // 使用实际的total数量来获取所有数据
        return await this.getDoctorPatientList({
          current: 1,
          size: firstResult.total // 使用实际总数
        })
      } else {
        console.log('患者总数较少或获取失败，使用首次结果')
        return firstResult
      }
    } catch (error) {
      console.error('获取医生所有绑定患者异常:', error)
      return {
        success: false,
        message: '获取患者列表失败',
        data: [],
        total: 0
      }
    }
  }

  /**
   * 根据患者ID获取患者详细信息
   * @param {number} patientId - 患者ID
   * @returns {Promise<Object>} 患者详细信息
   */
  async getPatientDetail(patientId) {
    try {
      console.log('=== 调用获取患者详细信息API ===')
      console.log('患者ID:', patientId)
      console.log('API URL:', `/doctor/patient/info/${patientId}`)

      // 调用后端获取患者详细信息接口
      const response = await apiClient.get(`/doctor/patient/info/${patientId}`)

      // 检查响应数据结构
      const data = response.data
      console.log('🔍 获取患者详细信息API完整响应:', JSON.stringify(data, null, 2))
      console.log('🔍 响应code:', data.code, '类型:', typeof data.code)
      console.log('🔍 响应message:', data.message)
      console.log('🔍 响应data:', data.data)

      // 根据实际API返回，code为0表示成功
      if (data.code === 0) {
        console.log('✅ code === 0，返回成功')
        return {
          success: true,
          message: data.message || '获取患者信息成功',
          data: data.data || {}
        }
      } else {
        // 获取失败
        const errorMessage = data.message || data.msg || '获取患者信息失败'
        console.warn('❌ code !== 0，获取患者详细信息失败:', errorMessage)
        console.warn('❌ 实际code:', data.code)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: null
        }
      }
    } catch (error) {
      console.error('获取患者详细信息异常:', error)

      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || errorData?.msg || `服务器错误 (${error.response.status})`

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status,
          data: null
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络',
          data: null
        }
      } else {
        // 请求设置时发生错误
        return {
          success: false,
          message: `请求错误: ${error.message}`,
          data: null
        }
      }
    }
  }

  /**
   * 更新患者信息
   * @param {number} patientId - 患者ID
   * @param {Object} patientData - 患者数据
   * @returns {Promise<Object>} 更新结果
   */
  async updatePatient(patientId, patientData) {
    try {
      console.log('调用患者信息更新API:', { patientId, patientData })

      // 基于现有接口模式，尝试可能的更新接口路径
      // 常见的患者更新接口可能是：
      // PUT /user/update-patient/{id}
      // PUT /user/patient/{id}
      // POST /user/update-patient (with id in body)

      // 先尝试最可能的接口路径
      const response = await apiClient.put(`/user/update-patient/${patientId}`, patientData)

      // 检查响应数据结构
      const data = response.data
      console.log('患者信息更新API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '患者信息更新成功',
          data: data.data
        }
      } else {
        // 更新失败
        const errorMessage = data.message || '患者信息更新失败'
        console.warn('患者信息更新失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code
        }
      }
    } catch (error) {
      console.error('更新患者信息API调用失败:', error)
      console.error('错误详情:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      })

      // 处理网络错误或服务器错误
      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络设置'
        }
      } else {
        // 其他错误
        return {
          success: false,
          message: error.message || '更新患者信息失败，请重试'
        }
      }
    }
  }

  /**
   * 删除患者
   * @param {number} patientId - 患者ID
   * @returns {Promise<Object>} 删除结果
   */
  async deletePatient(patientId) {
    try {
      const response = await apiClient.delete(`/patient/${patientId}`)
      const data = response.data

      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '患者删除成功'
        }
      } else {
        return {
          success: false,
          message: data.message || '患者删除失败'
        }
      }
    } catch (error) {
      console.error('删除患者失败:', error)
      return {
        success: false,
        message: '删除患者失败'
      }
    }
  }

  /**
   * 获取某个患者睡眠日记列表
   * @param {string|number} patientId - 患者ID
   * @param {Object} [queryParams] - 查询参数
   * @param {string} [queryParams.startDate] - 开始日期 (YYYY-MM-DD)
   * @param {string} [queryParams.endDate] - 结束日期 (YYYY-MM-DD)
   * @returns {Promise<Object>} 睡眠日记列表
   */
  async getSleepDiaryList(patientId, queryParams = {}) {
    try {
      console.log('调用获取睡眠日记列表API:', { patientId, queryParams })

      // 构建请求参数
      const params = {
        id: patientId
      }

      // 添加日期范围参数
      if (queryParams.startDate) {
        params.startDate = queryParams.startDate
      }
      if (queryParams.endDate) {
        params.endDate = queryParams.endDate
      }

      // 调用后端睡眠日记列表接口 /sleep-diary/list
      console.log('发送请求到 /sleep-diary/list，参数:', params)

      let response
      try {
        // 首先尝试 /sleep-diary/list 接口
        response = await apiClient.get('/sleep-diary/list', {
          params
        })
      } catch (firstError) {
        console.log('第一个接口路径失败，尝试备用路径:', firstError.message)

        // 如果第一个路径失败，尝试其他可能的路径
        try {
          response = await apiClient.get('/sleep/diary/list', {
            params
          })
          console.log('使用备用路径 /sleep/diary/list 成功')
        } catch (secondError) {
          console.log('备用路径也失败，尝试第三个路径:', secondError.message)

          // 尝试第三个可能的路径
          response = await apiClient.get('/diary/list', {
            params
          })
          console.log('使用第三个路径 /diary/list 成功')
        }
      }

      // 检查响应数据结构
      const data = response.data
      console.log('获取睡眠日记列表API响应详情:', data)
      console.log('响应状态码:', response.status)
      console.log('响应头:', response.headers)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '获取睡眠日记列表成功',
          data: data.data || []
        }
      } else {
        // 获取失败
        const errorMessage = data.message || '获取睡眠日记列表失败'
        console.warn('获取睡眠日记列表失败:', errorMessage)
        console.warn('错误代码:', data.code)
        console.warn('完整响应数据:', data)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code
        }
      }
    } catch (error) {
      console.error('获取睡眠日记列表API调用失败:', error)

      // 处理网络错误或服务器错误
      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        console.error('服务器响应错误:', {
          status: error.response.status,
          data: errorData,
          headers: error.response.headers
        })

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        console.error('网络请求失败:', error.request)
        return {
          success: false,
          message: '网络连接失败，请检查网络设置'
        }
      } else {
        // 其他错误
        console.error('其他错误:', error.message)
        return {
          success: false,
          message: error.message || '获取睡眠日记列表失败，请重试'
        }
      }
    }
  }

  /**
   * 获取睡眠日记平均数据
   * @param {string|number} patientId - 患者ID
   * @param {Object} queryParams - 查询参数
   * @param {string} queryParams.startDate - 开始日期 (YYYY-MM-DD)
   * @param {string} queryParams.endDate - 结束日期 (YYYY-MM-DD)
   * @returns {Promise<Object>} 睡眠日记平均数据
   */
  async getSleepDiarySummary(patientId, queryParams) {
    try {
      console.log('调用获取睡眠日记平均数据API:', { patientId, queryParams })

      // 验证必需参数
      if (!patientId) {
        throw new Error('患者ID不能为空')
      }
      if (!queryParams.startDate || !queryParams.endDate) {
        throw new Error('开始日期和结束日期不能为空')
      }

      // 构建请求参数
      const params = {
        startDate: queryParams.startDate,
        endDate: queryParams.endDate
      }

      console.log('发送请求到 /sleep-diary/patient/summary，参数:', params)

      // 调用后端睡眠日记平均数据接口
      const response = await apiClient.get('/sleep-diary/patient/summary', {
        params,
        headers: {
          'Patient-Id': patientId // 通过Header传递患者ID
        }
      })

      // 检查响应数据结构
      const data = response.data
      console.log('获取睡眠日记平均数据API响应详情:', data)
      console.log('响应状态码:', response.status)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        console.log('✅ 获取睡眠日记平均数据成功')
        
        return {
          success: true,
          data: data.data,
          message: data.message || '获取平均数据成功'
        }
      } else {
        console.error('❌ 获取睡眠日记平均数据失败:', data.message)
        return {
          success: false,
          message: data.message || '获取平均数据失败',
          data: null
        }
      }
    } catch (error) {
      console.error('获取睡眠日记平均数据异常:', error)
      
      // 根据错误类型提供更具体的错误信息
      let errorMessage = '获取平均数据失败'
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = '网络连接失败，请检查网络连接'
      } else if (error.message && error.message.includes('500')) {
        errorMessage = '服务器内部错误，请稍后重试'
      } else if (error.message && error.message.includes('401')) {
        errorMessage = '认证失败，请重新登录'
      } else if (error.message && error.message.includes('403')) {
        errorMessage = '权限不足，无法访问该资源'
      }

      return {
        success: false,
        message: errorMessage,
        data: null
      }
    }
  }

  /**
   * 添加睡眠处方
   * @param {Object} prescriptionData - 睡眠处方数据
   * @param {number} prescriptionData.patientId - 患者ID
   * @param {string} prescriptionData.bedtime - 上床时间（HH:mm:ss）
   * @param {string} prescriptionData.wakeUpTime - 起床时间（HH:mm:ss）
   * @param {string} prescriptionData.prescriptionSleepDuration - 处方睡眠时长（HH:mm:ss）
   * @param {string} prescriptionData.prescriptionEndTime - 处方结束时间（ISO 8601）
   * @param {number} prescriptionData.isUse - 是否当前使用（1-是，0-否）
   * @returns {Promise<Object>} 添加结果
   */
  async addSleepPrescription(prescriptionData) {
    try {
      console.log('调用添加睡眠处方API:', prescriptionData)

      // 调用后端添加睡眠处方接口 /sleep-prescription/add
      const response = await apiClient.post('/sleep-prescription/add', prescriptionData)

      // 检查响应数据结构
      const data = response.data
      console.log('添加睡眠处方API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '添加睡眠处方成功',
          data: data.data
        }
      } else {
        // 添加失败
        const errorMessage = data.message || '添加睡眠处方失败'
        console.warn('添加睡眠处方失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code
        }
      }
    } catch (error) {
      console.error('添加睡眠处方异常:', error)

      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络'
        }
      } else {
        // 其他错误
        return {
          success: false,
          message: error.message || '添加睡眠处方失败，请重试'
        }
      }
    }
  }

  /**
   * 获取患者睡眠处方列表
   * @param {string|number} patientId - 患者ID
   * @returns {Promise<Object>} 睡眠处方列表
   */
  async getSleepPrescriptionList(patientId) {
    try {
      console.log('调用获取睡眠处方列表API:', { patientId })

      // 调用后端睡眠处方列表接口（假设接口路径）
      const response = await apiClient.get('/sleep-prescription/list', {
        params: {
          patientId: patientId
        }
      })

      // 检查响应数据结构
      const data = response.data
      console.log('获取睡眠处方列表API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '获取睡眠处方列表成功',
          data: data.data || []
        }
      } else {
        // 获取失败
        const errorMessage = data.message || '获取睡眠处方列表失败'
        console.warn('获取睡眠处方列表失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: []
        }
      }
    } catch (error) {
      console.error('获取睡眠处方列表异常:', error)

      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status,
          data: []
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络',
          data: []
        }
      } else {
        // 其他错误
        return {
          success: false,
          message: error.message || '获取睡眠处方列表失败，请重试',
          data: []
        }
      }
    }
  }

  /**
   * 医生编辑患者个人信息
   * @param {number} patientId - 患者ID
   * @param {Object} patientData - 患者信息数据
   * @param {string} patientData.phone - 手机号（必选）
   * @param {string} patientData.name - 姓名（必选）
   * @param {number} patientData.gender - 性别：1-男，2-女（必选）
   * @param {number} patientData.age - 年龄（必选）
   * @param {number} [patientData.height] - 身高（cm）
   * @param {number} [patientData.weight] - 体重（kg）
   * @param {number} [patientData.bmi] - BMI
   * @param {string} [patientData.education] - 文化程度
   * @param {string} [patientData.profession] - 职业
   * @param {number} [patientData.isMarried] - 婚否：1-是，0-否
   * @param {string} [patientData.idCard] - 身份证号
   * @param {string} [patientData.nation] - 民族
   * @param {string} [patientData.nickname] - 昵称
   * @param {string} [patientData.patientNo] - 患者编号
   * @param {number} [patientData.isMedication] - 是否服药：1-是，0-否
   * @param {number} [patientData.isRemind] - 是否开启提醒：1-是，0-否
   * @param {string} [patientData.complaint] - 主诉
   * @param {string} [patientData.registerTime] - 注册时间
   * @param {string} [patientData.updateTime] - 更新时间
   * @param {string} [patientData.uploadTime] - 数据上传时间
   * @param {string} [patientData.wakeUpTime] - 起床时间
   * @param {string} [patientData.state] - 状态：0-治疗中，1-已完成
   * @param {string} [patientData.clinicalDiagnosis] - 临床诊断
   * @param {Array|null} [patientData.tags] - 标签列表
   * @returns {Promise<Object>} 编辑结果
   */
  async editPatientInfo(patientId, patientData) {
    try {
      console.log('=== 医生编辑患者个人信息API ===')
      console.log('患者ID:', patientId)
      console.log('患者ID类型:', typeof patientId)
      console.log('编辑数据:', patientData)
      console.log('API URL:', `/doctor/patient/edit/${patientId}`)

      // 验证必填参数
      const requiredFields = ['phone', 'name', 'gender', 'age']
      for (const field of requiredFields) {
        if (patientData[field] === undefined || patientData[field] === null || patientData[field] === '') {
          return {
            success: false,
            message: `${field} 是必填字段`
          }
        }
      }

      // 验证性别值
      if (![1, 2].includes(patientData.gender)) {
        return {
          success: false,
          message: '请选择正确的性别'
        }
      }

      // 验证年龄
      if (!Number.isInteger(patientData.age) || patientData.age < 0 || patientData.age > 150) {
        return {
          success: false,
          message: '年龄必须是0-150之间的整数'
        }
      }

      // 验证手机号格式
      const phoneRegex = /^1[2-9]\d{9}$/
      if (!phoneRegex.test(patientData.phone)) {
        return {
          success: false,
          message: '请输入正确的手机号格式'
        }
      }

      // 构建请求数据，确保所有字段都按照接口文档格式
      const requestData = {
        phone: patientData.phone,
        name: patientData.name,
        gender: patientData.gender,
        age: patientData.age,
        height: patientData.height || null,
        weight: patientData.weight || null,
        bmi: patientData.bmi || null,
        education: patientData.education || null,
        profession: patientData.profession || null,
        isMarried: patientData.isMarried || null,
        idCard: patientData.idCard || null,
        nation: patientData.nation || null,
        nickname: patientData.nickname || null,
        patientNo: patientData.patientNo || null,
        isMedication: patientData.isMedication || null,
        isRemind: patientData.isRemind || null,
        complaint: patientData.complaint || null,
        registerTime: patientData.registerTime || null,
        updateTime: patientData.updateTime || null,
        uploadTime: patientData.uploadTime || null,
        wakeUpTime: patientData.wakeUpTime || null,
        state: patientData.state || null,
        clinicalDiagnosis: patientData.clinicalDiagnosis || null,
        tags: patientData.tags || null
      }

      console.log('发送请求到:', `/doctor/patient/edit/${patientId}`)
      console.log('请求数据:', JSON.stringify(requestData, null, 2))

      // 调用后端编辑患者信息接口
      const response = await apiClient.put(`/doctor/patient/edit/${patientId}`, requestData)

      console.log('API响应:', response)

      // 检查响应数据结构
      const data = response.data || response
      console.log('响应数据:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        console.log('✅ 编辑患者信息成功')
        return {
          success: true,
          message: data.msg || '患者信息更新成功',
          data: data.data
        }
      } else {
        console.error('❌ 编辑患者信息失败:', data.msg)
        return {
          success: false,
          message: data.msg || '患者信息更新失败',
          errorCode: data.code
        }
      }
    } catch (error) {
      console.error('❌ 编辑患者信息异常:', error)

      let errorMessage = '编辑患者信息失败'

      if (error.response) {
        console.error('API响应错误:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        })

        const errorData = error.response.data || {}

        // 根据状态码提供更具体的错误信息
        if (error.response.status === 401) {
          errorMessage = '认证失败，请重新登录'
        } else if (error.response.status === 403) {
          errorMessage = '权限不足，无法编辑患者信息'
        } else if (error.response.status === 404) {
          errorMessage = '患者不存在或已被删除'
        } else if (error.response.status === 500) {
          errorMessage = '服务器内部错误，请稍后重试'
        } else {
          errorMessage = errorData.msg || errorData.message || `服务器错误 (${error.response.status})`
        }
      } else if (error.request) {
        console.error('网络请求错误:', error.request)
        errorMessage = '网络连接失败，请检查网络设置'
      } else {
        console.error('请求配置错误:', error.message)
        errorMessage = error.message || '请求配置失败'
      }

      return {
        success: false,
        message: errorMessage,
        error: error
      }
    }
  }

  /**
   * 添加医嘱记录
   * @param {Object} medicalOrderData - 医嘱记录数据
   * @param {number} medicalOrderData.patientId - 患者ID
   * @param {string} medicalOrderData.taskName - 任务名称
   * @param {string} medicalOrderData.taskContent - 任务内容描述
   * @param {string} medicalOrderData.completionTime - 任务完成时间（ISO格式）
   * @param {number} medicalOrderData.completionStatus - 是否完成（0-未完成，1-已完成）
   * @returns {Promise<Object>} 添加结果
   */
  async addMedicalOrderRecord(medicalOrderData) {
    try {
      console.log('=== 添加医嘱记录API调用详细调试 ===')
      console.log('原始传入数据:', medicalOrderData)
      console.log('patientId值:', medicalOrderData.patientId)
      console.log('patientId类型:', typeof medicalOrderData.patientId)
      console.log('API URL:', '/medical-order-record/add')
      console.log('请求数据 (JSON):', JSON.stringify(medicalOrderData, null, 2))

      // 检查请求头中的token
      const token = localStorage.getItem('cbti_token')
      console.log('请求使用的token:', token)

      // 如果token存在，尝试解析看看里面的信息
      if (token) {
        try {
          // JWT token的payload部分是base64编码的
          const tokenParts = token.split('.')
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]))
            console.log('Token中的payload信息:', payload)
          }
        } catch (e) {
          console.log('无法解析token:', e)
        }
      }

      // 调用后端添加医嘱记录接口 /medical-order-record/add
      const response = await apiClient.post('/medical-order-record/add', medicalOrderData)

      // 检查响应数据结构
      const data = response.data
      console.log('添加医嘱记录API响应详情:', data)
      console.log('响应状态码:', response.status)
      console.log('完整响应:', response)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '添加医嘱记录成功',
          data: data.data
        }
      } else {
        // 添加失败
        const errorMessage = data.message || '添加医嘱记录失败'
        console.warn('添加医嘱记录失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: null
        }
      }
    } catch (error) {
      console.error('添加医嘱记录异常:', error)

      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status,
          data: null
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络',
          data: null
        }
      } else {
        // 其他错误
        return {
          success: false,
          message: error.message || '添加医嘱记录失败，请重试',
          data: null
        }
      }
    }
  }

  /**
   * 获取患者医嘱记录列表
   * @param {number} patientId - 患者ID
   * @returns {Promise<Object>} 医嘱记录列表
   */
  async getMedicalOrderList(patientId) {
    try {
      console.log('调用获取患者医嘱记录列表API:', patientId)

      // 调用后端获取医嘱记录列表接口
      const response = await apiClient.get(`/medical-order-record/list?patientId=${patientId}`)

      // 检查响应数据结构
      const data = response.data
      console.log('获取患者医嘱记录列表API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '获取医嘱记录列表成功',
          data: data.data || []
        }
      } else {
        // 获取失败
        const errorMessage = data.message || '获取医嘱记录列表失败'
        console.warn('获取医嘱记录列表失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: []
        }
      }
    } catch (error) {
      console.error('获取医嘱记录列表异常:', error)

      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status,
          data: []
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络',
          data: []
        }
      } else {
        // 其他错误
        return {
          success: false,
          message: error.message || '获取医嘱记录列表失败，请重试',
          data: []
        }
      }
    }
  }

  /**
   * 删除医嘱记录
   * @param {number} id - 医嘱记录ID
   * @returns {Promise<Object>} 删除结果
   */
  async deleteMedicalOrderRecord(id) {
    try {
      console.log('=== 调用删除医嘱记录API ===')
      console.log('医嘱记录ID:', id)
      console.log('医嘱记录ID类型:', typeof id)

      // 尝试多种URL格式，根据常见的RESTful API设计
      const urlOptions = [
        `/medical-order-record/delete/${id}`, // 路径参数
        `/medical-order-record/delete?id=${id}`, // 查询参数
        `/medical-order-record/${id}` // RESTful风格
      ]

      console.log('尝试的URL选项:', urlOptions)

      // 先尝试最常见的RESTful风格
      let response
      let lastError

      for (let i = 0; i < urlOptions.length; i++) {
        try {
          console.log(`尝试第${i + 1}种URL格式:`, urlOptions[i])
          response = await apiClient.delete(urlOptions[i])
          console.log(`第${i + 1}种URL格式成功`)
          break
        } catch (error) {
          console.log(`第${i + 1}种URL格式失败:`, error.response?.status, error.response?.data)
          lastError = error
          if (i === urlOptions.length - 1) {
            throw lastError
          }
        }
      }

      // 检查响应数据结构
      const data = response.data
      console.log('删除医嘱记录API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '删除医嘱记录成功',
          data: data.data
        }
      } else {
        // 删除失败
        const errorMessage = data.message || '删除医嘱记录失败'
        console.warn('删除医嘱记录失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: null
        }
      }
    } catch (error) {
      console.error('删除医嘱记录异常:', error)

      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status,
          data: null
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络',
          data: null
        }
      } else {
        // 请求设置时发生错误
        return {
          success: false,
          message: `请求错误: ${error.message}`,
          data: null
        }
      }
    }
  }

  /**
   * 更新医嘱记录
   * @param {Object} medicalOrderData - 医嘱记录数据
   * @param {number} medicalOrderData.id - 医嘱记录ID
   * @param {number} medicalOrderData.doctorId - 医生ID
   * @param {number} medicalOrderData.patientId - 患者ID
   * @param {string} medicalOrderData.taskName - 任务名称
   * @param {string} medicalOrderData.taskContent - 任务内容描述
   * @param {string} medicalOrderData.completionTime - 任务完成时间（ISO格式）
   * @param {number} medicalOrderData.completionStatus - 是否完成（0-未完成，1-已完成）
   * @returns {Promise<Object>} 更新结果
   */
  async updateMedicalOrderRecord(medicalOrderData) {
    try {
      console.log('=== 调用更新医嘱记录API ===')
      console.log('更新数据:', medicalOrderData)
      console.log('API URL:', '/medical-order-record/update')
      console.log('请求数据:', JSON.stringify(medicalOrderData, null, 2))

      // 调用后端更新医嘱记录接口
      const response = await apiClient.put('/medical-order-record/update', medicalOrderData)

      // 检查响应数据结构
      const data = response.data
      console.log('更新医嘱记录API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '更新医嘱记录成功',
          data: data.data
        }
      } else {
        // 更新失败
        const errorMessage = data.message || '更新医嘱记录失败'
        console.warn('更新医嘱记录失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: null
        }
      }
    } catch (error) {
      console.error('更新医嘱记录异常:', error)

      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status,
          data: null
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络',
          data: null
        }
      } else {
        // 请求设置时发生错误
        return {
          success: false,
          message: `请求错误: ${error.message}`,
          data: null
        }
      }
    }
  }

  /**
   * 获取医生管理的所有患者医嘱记录列表
   * @param {Object} params - 查询参数（可选，用于前端筛选）
   * @returns {Promise<Object>} 医嘱记录列表
   */
  async getAllMedicalOrderList(params = {}) {
    try {
      console.log('=== 调用获取医生所有患者医嘱记录列表API ===')
      console.log('查询参数:', params)
      console.log('API URL:', '/medical-order-record/doctor-list')

      // 根据接口文档，这个接口不需要查询参数，直接获取当前医生的所有患者医嘱记录
      const response = await apiClient.get('/medical-order-record/doctor-list')

      // 检查响应数据结构
      const data = response.data
      console.log('获取所有医嘱记录列表API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '获取医嘱记录列表成功',
          data: data.data || [],
          total: data.total || 0
        }
      } else {
        // 获取失败
        const errorMessage = data.message || '获取医嘱记录列表失败'
        console.warn('获取医嘱记录列表失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: [],
          total: 0
        }
      }
    } catch (error) {
      console.error('获取医嘱记录列表异常:', error)

      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status,
          data: [],
          total: 0
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络',
          data: [],
          total: 0
        }
      } else {
        // 其他错误
        return {
          success: false,
          message: error.message || '获取医嘱记录列表失败，请重试',
          data: [],
          total: 0
        }
      }
    }
  }

  /**
   * 绑定患者与医生
   * @param {Object} bindingData - 绑定数据
   * @param {number} bindingData.patientId - 患者ID
   * @param {number} bindingData.doctorId - 医生ID
   * @param {string} [bindingData.bindingType] - 绑定类型（主治医生、会诊医生等）
   * @param {string} [bindingData.remark] - 备注
   * @returns {Promise<Object>} 绑定结果
   */
  async bindPatientToDoctor(bindingData) {
    try {
      console.log('调用患者医生绑定API:', bindingData)

      // 调用后端患者医生绑定接口
      const response = await apiClient.post('/patient-doctor/bind', bindingData)

      // 检查响应数据结构
      const data = response.data
      console.log('患者医生绑定API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '绑定成功',
          data: data.data || {}
        }
      } else {
        // 绑定失败
        const errorMessage = data.message || '绑定失败'
        console.warn('患者医生绑定失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code
        }
      }
    } catch (error) {
      console.error('患者医生绑定异常:', error)

      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络'
        }
      } else {
        // 其他错误
        return {
          success: false,
          message: error.message || '绑定失败，请重试'
        }
      }
    }
  }

  /**
   * 解绑患者与医生
   * @param {Object} unbindingData - 解绑数据
   * @param {number} unbindingData.patientId - 患者ID
   * @param {number} unbindingData.doctorId - 医生ID
   * @returns {Promise<Object>} 解绑结果
   */
  async unbindPatientFromDoctor(unbindingData) {
    try {
      console.log('调用患者医生解绑API:', unbindingData)

      // 调用后端患者医生解绑接口
      const response = await apiClient.post('/patient-doctor/unbind', unbindingData)

      // 检查响应数据结构
      const data = response.data
      console.log('患者医生解绑API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '解绑成功',
          data: data.data || {}
        }
      } else {
        // 解绑失败
        const errorMessage = data.message || '解绑失败'
        console.warn('患者医生解绑失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code
        }
      }
    } catch (error) {
      console.error('患者医生解绑异常:', error)

      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络'
        }
      } else {
        // 其他错误
        return {
          success: false,
          message: error.message || '解绑失败，请重试'
        }
      }
    }
  }

  /**
   * 获取患者的医生绑定关系
   * @param {number} patientId - 患者ID
   * @returns {Promise<Object>} 绑定关系列表
   */
  async getPatientDoctorBindings(patientId) {
    try {
      console.log('调用获取患者医生绑定关系API:', patientId)

      // 调用后端获取患者医生绑定关系接口
      const response = await apiClient.get(`/patient-doctor/bindings/${patientId}`)

      // 检查响应数据结构
      const data = response.data
      console.log('获取患者医生绑定关系API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '获取绑定关系成功',
          data: data.data || []
        }
      } else {
        // 获取失败
        const errorMessage = data.message || '获取绑定关系失败'
        console.warn('获取患者医生绑定关系失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: []
        }
      }
    } catch (error) {
      console.error('获取患者医生绑定关系异常:', error)

      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status,
          data: []
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络',
          data: []
        }
      } else {
        // 其他错误
        return {
          success: false,
          message: error.message || '获取绑定关系失败，请重试',
          data: []
        }
      }
    }
  }

  /**
   * 批量绑定患者与医生
   * @param {Object} batchBindingData - 批量绑定数据
   * @param {number[]} batchBindingData.patientIds - 患者ID列表
   * @param {number} batchBindingData.doctorId - 医生ID
   * @param {string} [batchBindingData.bindingType] - 绑定类型
   * @param {string} [batchBindingData.remark] - 备注
   * @returns {Promise<Object>} 批量绑定结果
   */
  async batchBindPatientsToDoctor(batchBindingData) {
    try {
      console.log('调用批量患者医生绑定API:', batchBindingData)

      // 调用后端批量患者医生绑定接口
      const response = await apiClient.post('/patient-doctor/batch-bind', batchBindingData)

      // 检查响应数据结构
      const data = response.data
      console.log('批量患者医生绑定API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '批量绑定成功',
          data: data.data || {}
        }
      } else {
        // 批量绑定失败
        const errorMessage = data.message || '批量绑定失败'
        console.warn('批量患者医生绑定失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code
        }
      }
    } catch (error) {
      console.error('批量患者医生绑定异常:', error)

      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络'
        }
      } else {
        // 其他错误
        return {
          success: false,
          message: error.message || '批量绑定失败，请重试'
        }
      }
    }
  }

  /**
   * 删除患者
   * @param {string|number} patientId - 患者ID
   * @returns {Promise<Object>} 删除结果
   */
  async deletePatient(patientId) {
    try {
      console.log('=== 删除患者 ===')
      console.log('患者ID:', patientId)

      // 验证患者ID
      if (!patientId) {
        return {
          success: false,
          message: '患者ID不能为空'
        }
      }

      console.log('发送删除请求到:', `/doctor/patient/delete/${patientId}`)

      // 发送DELETE请求
      const response = await apiClient.delete(`/doctor/patient/delete/${patientId}`)

      console.log('删除患者API原始响应:', response)

      const data = response.data || response
      if (data.code === 0) {
        console.log('删除患者成功')
        return {
          success: true,
          message: data.message || '患者删除成功',
          data: data.data
        }
      } else {
        console.error('删除患者失败:', data)
        return {
          success: false,
          message: data.message || data.msg || '删除患者失败'
        }
      }
    } catch (error) {
      console.error('删除患者异常:', error)
      console.error('错误详情:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      })

      if (error.response) {
        const status = error.response.status
        const data = error.response.data

        switch (status) {
          case 401:
            return { success: false, message: '登录已过期，请重新登录' }
          case 403:
            return { success: false, message: '没有权限删除此患者' }
          case 404:
            return { success: false, message: '患者不存在或已被删除' }
          case 500:
            return { success: false, message: '服务器繁忙，请稍后重试' }
          default:
            return { success: false, message: data?.message || '删除患者失败' }
        }
      } else if (error.request) {
        return { success: false, message: '网络连接失败，请检查网络' }
      } else {
        return { success: false, message: '删除患者失败，请重试' }
      }
    }
  }
}

// 创建患者API实例
export const patientApi = new PatientApi()

// 默认导出
export default patientApi
